import { useContext, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AuthContext } from "../../context/AuthProvider";
import ApiInterface from "./api-interface";
import { API_HTTP, resumeAnalyzerApiEndpoints } from "./endpoints";
import {
	CatalogItem,
	CreateReferralPayload,
	CreateReferralResponse,
	CreditBalance,
	MyReferralCode,
	MyReferrerInfo,
	OriginatorSummary,
	RedeemReferralResponse,
	ValidateReferralResponse,
	PurchaseResponse,
	MailingAddress
} from "../../DataModels/DataModels";

function useAuthenticatedApi() {
	const user = useContext(AuthContext)?.user ?? null;
	const api = useMemo(
		() => (user ? new ApiInterface(API_HTTP.resumeAnalyzer, user) : null),
		[user]
	);
	return { user, api };
}

export function useCreateReferral() {
	const { api, user } = useAuthenticatedApi();
	const queryClient = useQueryClient();

	return useMutation<CreateReferralResponse, Error, CreateReferralPayload>({
		mutationFn: async (data) => {
			if (!api) throw new Error("Not authenticated");
			return api.post(resumeAnalyzerApiEndpoints.referrals, data);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["referrals"] });
			queryClient.invalidateQueries({
				queryKey: ["user", user?.uid, "contacts"]
			});
		}
	});
}

export function useMyReferrals(status?: string) {
	const { api, user } = useAuthenticatedApi();

	const query = useQuery<{ referrals: MyReferralCode[]; total: number }>({
		queryKey: ["referrals", user?.uid, status, api],
		queryFn: async () => {
			if (!api) throw new Error("Not authenticated");
			return api.get(
				resumeAnalyzerApiEndpoints.referrals,
				{},
				{
					params: { status, limit: 50 }
				}
			);
		},
		enabled: !!user,
		staleTime: 5 * 60 * 1000
	});

	return {
		referrals: query.data?.referrals ?? [],
		total: query.data?.total ?? 0,
		isLoading: query.isLoading
	};
}

export function useValidateReferralCode(code: string | null) {
	return useQuery<ValidateReferralResponse>({
		queryKey: ["referral-validate", code],
		queryFn: async () => {
			// Public — no auth required
			const api = new ApiInterface(API_HTTP.resumeAnalyzer);
			return api.get(resumeAnalyzerApiEndpoints.referralValidate, {
				code: code!
			});
		},
		enabled: !!code
	});
}

export function useRedeemReferral() {
	const queryClient = useQueryClient();

	return useMutation<
		RedeemReferralResponse,
		Error,
		{ authenticatedUser: any; code: string }
	>({
		mutationFn: async ({ authenticatedUser, code }) => {
			const api = new ApiInterface(API_HTTP.resumeAnalyzer, authenticatedUser);
			return api.post(resumeAnalyzerApiEndpoints.referralRedeem, {}, { code });
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["referrals"] });
			queryClient.invalidateQueries({ queryKey: ["credits"] });
		}
	});
}

export function useRevokeReferral() {
	const { api } = useAuthenticatedApi();
	const queryClient = useQueryClient();

	return useMutation<{ status: string }, Error, string>({
		mutationFn: async (code) => {
			if (!api) throw new Error("Not authenticated");
			return api.delete(
				resumeAnalyzerApiEndpoints.referralRevoke,
				{},
				{ code }
			);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["referrals"] });
		}
	});
}

export function useCreditStoreCatalog() {
	const { api, user } = useAuthenticatedApi();

	const query = useQuery<{ items: CatalogItem[] }>({
		queryKey: ["credit-catalog", user?.uid, api],
		queryFn: async () => {
			if (!api) throw new Error("Not authenticated");
			return api.get(resumeAnalyzerApiEndpoints.referralCatalog);
		},
		enabled: !!user,
		staleTime: 5 * 60 * 1000
	});

	return {
		catalogItems: query.data?.items ?? [],
		isLoading: query.isLoading
	};
}

export function useCreditBalance() {
	const { api, user } = useAuthenticatedApi();

	const query = useQuery<CreditBalance>({
		queryKey: ["credits", user?.uid, api],
		queryFn: async () => {
			if (!api) throw new Error("Not authenticated");
			return api.get(resumeAnalyzerApiEndpoints.referralCredits);
		},
		enabled: !!user,
		staleTime: 5 * 60 * 1000
	});

	return {
		balance: query.data?.balance ?? 0,
		totalEarned: query.data?.totalEarned ?? 0,
		pendingPhases: query.data?.pendingPhases ?? [],
		isLoading: query.isLoading
	};
}

export function useMyReferrer() {
	const { api, user } = useAuthenticatedApi();

	return useQuery<MyReferrerInfo>({
		queryKey: ["my-referrer", user?.uid, api],
		queryFn: async () => {
			if (!api) throw new Error("Not authenticated");
			return api.get(resumeAnalyzerApiEndpoints.referralMyReferrer);
		},
		enabled: !!user,
		staleTime: 5 * 60 * 1000
	});
}

export function useOriginatorSummary() {
	const { api, user } = useAuthenticatedApi();

	return useQuery<OriginatorSummary>({
		queryKey: ["originator-summary", user?.uid, api],
		queryFn: async () => {
			if (!api) throw new Error("Not authenticated");
			return api.get(resumeAnalyzerApiEndpoints.referralOriginatorSummary);
		},
		enabled: !!user,
		staleTime: 5 * 60 * 1000
	});
}

export function usePurchaseCredits() {
	const { api } = useAuthenticatedApi();
	const queryClient = useQueryClient();

	return useMutation<
		PurchaseResponse,
		Error,
		{ itemKey: string; mailingAddress: MailingAddress }
	>({
		mutationFn: async (data) => {
			if (!api) throw new Error("Not authenticated");
			return api.post(resumeAnalyzerApiEndpoints.referralPurchase, data);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["credits"] });
		}
	});
}
